import { useContext, useMemo } from 'react';
import { canQueueMultipleLevels, getQueueLimit, QUEUE_LIMITS, type BuildingType, type Planet } from '@kaeliux/shared';
import { extractScienceLevels } from '@kaeliux/game-engine';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore, selectEffectiveTier } from '../store/playerStore';
import { upgradeBuilding, queueMaxBuildingLevels, cancelBuildingUpgrade, type PlanetActionOptions } from '../services/planet.service';
import { BUILDING_SCREEN_ROUTE } from '../components/planet/buildingScreens';
import { hasHigherQueueTier } from '../utils/queueUpsell';
import { TeamPlanetViewContext, zoneRouteFrom, type PlanetView } from './TeamPlanetViewContext';

const personalZoneRoute = zoneRouteFrom('');

async function upgradePersonalBuilding(planet: Planet, buildingType: BuildingType, options: PlanetActionOptions, plotId?: string) {
    const updated = await upgradeBuilding(planet.id, buildingType, options, plotId);
    usePlanetStore.setState({ currentPlanet: updated });
}

/** Returns how many levels the server actually queued — it decides the count, not the client preview. */
async function queueMaxPersonalBuildingLevels(planet: Planet, buildingType: BuildingType, options: PlanetActionOptions, plotId?: string, maxLevels?: number) {
    const { planet: updated, queuedCount } = await queueMaxBuildingLevels(planet.id, buildingType, options, plotId, maxLevels);
    usePlanetStore.setState({ currentPlanet: updated });
    return queuedCount;
}

async function cancelPersonalBuildingUpgrade(planet: Planet, eventId: number, options: PlanetActionOptions) {
    const updated = await cancelBuildingUpgrade(planet.id, eventId, options);
    usePlanetStore.setState({ currentPlanet: updated });
}

/** The active `PlanetView`: the Team planet's inside `TeamPlanetLayout`, otherwise the player's own planet. */
export function usePlanetView(): PlanetView {
    const teamView = useContext(TeamPlanetViewContext);
    const sciences = usePlayerStore((s) => s.sciences);
    const effectiveTier = usePlayerStore(selectEffectiveTier);

    const personalView = useMemo<PlanetView>(() => ({
        kind: 'personal',
        ready: sciences !== null,
        canManage: true,
        readOnlyReason: null,
        allowRush: true,
        allowDestroy: true,
        scienceLevels: extractScienceLevels(sciences ?? undefined),
        buildingQueueLimit: getQueueLimit(effectiveTier),
        queueMaxAccess: canQueueMultipleLevels(effectiveTier) ? 'granted' : 'locked',
        canUnlockMoreSlots: hasHigherQueueTier(QUEUE_LIMITS, effectiveTier),
        homeRoute: '/planet',
        zoneRoute: personalZoneRoute,
        screenRoute: (buildingType) => BUILDING_SCREEN_ROUTE[buildingType],
        buildingRace: () => undefined,
        upgradeBuilding: upgradePersonalBuilding,
        queueMaxBuildingLevels: queueMaxPersonalBuildingLevels,
        cancelBuildingUpgrade: cancelPersonalBuildingUpgrade,
    }), [sciences, effectiveTier]);

    return teamView ?? personalView;
}
